import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from ".././popover";
import { format } from "date-fns/format";
import { Button } from ".././button";
import { cn } from "../../lib/utils";
import CustomCalendar from ".././customs/calendar";
import { useEffect } from "react";

type DisabledDateInfo = {
  date: Date;
  tooltipText: string;
};

const DEFAULT_DATE_FORMAT = "MM/dd/yyyy";

export function DatePickerV3({
  value,
  onChange,
  disabled,
  ariaLabel,
  className,
  placeholder = "Pick a date",
  primaryColor = "#10b981",
  disableFromDate,
  disableToDate,
  customDisabledDates,
  dateFormat = "yyyy-MM-dd",
  initialSelectedDate = null,
}: {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  placeholder?: string;
  primaryColor?: string;
  disableFromDate?: Date;
  disableToDate?: Date;
  customDisabledDates?: DisabledDateInfo[];
  dateFormat?: string;
  initialSelectedDate?: Date | null;
}) {
  const [open, setOpen] = React.useState(false);
  const [initialDataDone, setInitialDataDone] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    initialSelectedDate ?? undefined,
  );

  useEffect(() => {
    if (!initialDataDone && initialSelectedDate) {
      setSelectedDate(initialSelectedDate ?? undefined);
      setInitialDataDone(true);
    }
  }, [initialDataDone, initialSelectedDate]);

  // Sync with value prop when it changes from outside (but don't close the popup)
  useEffect(() => {
    setSelectedDate((currentValue) => {
      if (value?.getTime() === currentValue?.getTime()) return currentValue;
      return value;
    });
  }, [value]);

  const handleSelect = (date: Date) => {
    const nextDate = new Date(date);

    // Preserve the existing time portion when the user only changes the date.
    if (selectedDate) {
      nextDate.setHours(
        selectedDate.getHours(),
        selectedDate.getMinutes(),
        selectedDate.getSeconds(),
        selectedDate.getMilliseconds(),
      );
    }

    setSelectedDate(nextDate);
    onChange?.(nextDate);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal flex items-center gap-2",
            className,
          )}
          disabled={disabled}
          aria-label={ariaLabel}
        >
          {selectedDate ? format(selectedDate, dateFormat || DEFAULT_DATE_FORMAT) : <span>{placeholder}</span>}
          <div className="h-10 w-10 shrink-0 rounded-md flex items-center justify-center">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_16598_17461)">
                <path
                  d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H18C18.5304 5 19.0391 5.21071 19.4142 5.58579C19.7893 5.96086 20 6.46957 20 7V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V7Z"
                  stroke="#00C49A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 3V7"
                  stroke="#00C49A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 3V7"
                  stroke="#00C49A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 11H20"
                  stroke="#00C49A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 15H10V17H8V15Z"
                  stroke="#00C49A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_16598_17461">
                  <rect
                    width="24"
                    height="24"
                    fill="white"
                  />
                </clipPath>
              </defs>
            </svg>
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 border-none w-auto z-[9999]"
        align="start"
      >
        <CustomCalendar
          primaryColor={primaryColor}
          onSelect={handleSelect}
          disableFromDate={disableFromDate}
          disableToDate={disableToDate}
          customDisabledDates={customDisabledDates}
          initialSelectedDate={initialSelectedDate}
        />
      </PopoverContent>
    </Popover>
  );
}
